import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  locutorActual,
  programaActual,
  transmisionActual,
  transmisionStatusMeta,
  oyentesActuales,
  mensajes,
  encuestas,
  concursos,
  accionesConfig,
  statsCentro,
  reglasCentro,
  tipoMensajeMeta,
  estadoMensajeMeta,
  filtrarMensajesPorTipo,
  contarPorTipo,
} from '@/mocks/centroTransmision';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  return `Hace ${Math.floor(hrs / 24)}d`;
}

export default function CentroTransmisionSection() {
  const [activeTab, setActiveTab] = useState<string>('vista-general');
  const TX = transmisionActual;
  const statusInfo = transmisionStatusMeta[TX.status];

  const tabs = [
    { id: 'vista-general', label: 'Vista General', icono: 'ri-dashboard-line' },
    { id: 'transmision', label: 'Transmisión', icono: 'ri-broadcast-line' },
    { id: 'acciones', label: 'Acciones', icono: 'ri-flashlight-line' },
    { id: 'reglas', label: 'Reglas', icono: 'ri-scales-line' },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            CENTRO DE TRANSMISIÓN
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Todo el control en una sola pantalla
          </h2>
          <p className="text-foreground-500 text-sm max-w-[600px] mx-auto leading-relaxed">
            Controlá la transmisión, interactuá con los oyentes y gestioná concursos sin abrir menús. La cabina es el corazón de tu radio.
          </p>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { icono: 'ri-user-line', label: 'Oyentes Ahora', valor: oyentesActuales.actuales.toLocaleString(), color: 'text-emerald-600', bg: 'bg-emerald-100' },
            { icono: 'ri-chat-3-line', label: 'Mensajes Hoy', valor: statsCentro.totalMensajes.toString(), color: 'text-violet-600', bg: 'bg-violet-100' },
            { icono: 'ri-trophy-line', label: 'Concursos Activos', valor: concursos.filter((c) => c.estado === 'activo').length.toString(), color: 'text-amber-600', bg: 'bg-amber-100' },
            { icono: 'ri-timer-line', label: 'Respuesta Promedio', valor: statsCentro.tiempoPromedioRespuesta, color: 'text-sky-600', bg: 'bg-sky-100' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${stat.icono} ${stat.color} text-sm`}></i>
              </div>
              <p className="text-foreground-950 font-bold text-lg">{stat.valor}</p>
              <p className="text-foreground-400 text-[10px]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1.5 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
              }`}
            >
              <i className={tab.icono}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'vista-general' && (
          <div className="space-y-6">
            {/* Top bar demo */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${statusInfo.bg} flex items-center justify-center`}>
                    <i className={`ri-broadcast-line ${statusInfo.text} text-lg`}></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${statusInfo.dot} ${statusInfo.pulso ? 'animate-pulse' : ''}`}></span>
                      <span className={`text-xs font-bold ${statusInfo.text}`}>{statusInfo.label}</span>
                      <span className="text-foreground-400 text-[10px]">&bull; {TX.uptime}</span>
                    </div>
                    <p className="text-foreground-400 text-[11px]">
                      {locutorActual.nombre} &bull; {programaActual.nombre} &bull; {oyentesActuales.actuales.toLocaleString()} oyentes
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {accionesConfig.slice(0, 3).map((acc) => (
                    <span key={acc.tipo} className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-semibold flex items-center gap-1">
                      <i className={`${acc.icono} text-foreground-400`}></i>
                      {acc.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Messages preview */}
            <div className="space-y-3">
              <h4 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
                <i className="ri-inbox-line text-accent-500"></i>
                Últimos mensajes
              </h4>
              {mensajes.filter((m) => !m.bloqueado).slice(0, 4).map((msg) => {
                const tipoMeta = tipoMensajeMeta[msg.tipo];
                const estadoMeta = estadoMensajeMeta[msg.estado];
                return (
                  <div key={msg.id} className="bg-background-50 rounded-2xl border border-background-200 p-4">
                    <div className="flex items-start gap-3">
                      <img
                        src={msg.avatar}
                        alt={msg.remitente}
                        className="w-10 h-10 rounded-xl object-cover shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-foreground-950 font-bold text-xs">{msg.remitente}</span>
                          <span className={`px-1.5 py-0.5 rounded-md ${tipoMeta.color} text-[9px] font-semibold`}>
                            {tipoMeta.label}
                          </span>
                          {msg.destacado && (
                            <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-semibold">
                              Destacado
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 rounded-md ${estadoMeta.bg} ${estadoMeta.text} text-[9px] font-semibold`}>
                            {estadoMeta.label}
                          </span>
                        </div>
                        <p className="text-foreground-600 text-xs leading-relaxed line-clamp-2">{msg.contenido}</p>
                        {msg.cancionSolicitada && (
                          <p className="text-violet-600 text-[10px] font-semibold mt-1 flex items-center gap-1">
                            <i className="ri-music-line"></i>{msg.cancionSolicitada}
                          </p>
                        )}
                      </div>
                      <span className="text-foreground-400 text-[10px] shrink-0">{timeAgo(msg.timestamp)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'transmision' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status card */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5 space-y-4">
              <h4 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
                <i className="ri-pulse-line text-accent-500"></i>
                Estado de Transmisión
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Estado', valor: statusInfo.label, icono: 'ri-signal-tower-line' },
                  { label: 'Proveedor', valor: TX.proveedor, icono: 'ri-server-line' },
                  { label: 'Formato', valor: `${TX.formato} ${TX.bitrate}kbps`, icono: 'ri-sound-module-line' },
                  { label: 'Uptime', valor: TX.uptime, icono: 'ri-time-line' },
                ].map((item) => (
                  <div key={item.label} className="bg-background-100 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${item.icono} text-foreground-400 text-xs`}></i>
                      <span className="text-foreground-400 text-[10px]">{item.label}</span>
                    </div>
                    <p className="text-foreground-950 font-bold text-sm">{item.valor}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Program card */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5 space-y-4">
              <h4 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
                <i className="ri-calendar-event-line text-accent-500"></i>
                Programa Actual
              </h4>
              <div className="flex items-center gap-4">
                <img
                  src={locutorActual.avatar}
                  alt={locutorActual.nombre}
                  className="w-16 h-16 rounded-2xl object-cover shrink-0"
                  loading="lazy"
                />
                <div>
                  <p className="text-foreground-950 font-bold text-sm">{programaActual.nombre}</p>
                  <p className="text-foreground-500 text-xs">{programaActual.tipo}</p>
                  <p className="text-foreground-400 text-[10px]">{programaActual.horario}</p>
                  <div className="mt-2 h-1.5 bg-background-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${programaActual.progreso}%` }}
                    ></div>
                  </div>
                  <p className="text-foreground-400 text-[9px] mt-1">Faltan {programaActual.tiempoRestante}</p>
                </div>
              </div>
            </div>

            {/* Oyentes card */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5 space-y-4">
              <h4 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
                <i className="ri-group-line text-accent-500"></i>
                Oyentes
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Ahora', valor: oyentesActuales.actuales.toLocaleString(), sub: oyentesActuales.porcentaje },
                  { label: 'Pico Hoy', valor: oyentesActuales.picoHoy.toLocaleString(), sub: 'Máximo' },
                  { label: 'En Cabina', valor: oyentesActuales.enCabina.toLocaleString(), sub: 'Participando' },
                  { label: 'Escuchando', valor: oyentesActuales.escuchando.toLocaleString(), sub: 'Solo audio' },
                ].map((item) => (
                  <div key={item.label} className="bg-background-100 rounded-xl p-3">
                    <p className="text-foreground-400 text-[10px]">{item.label}</p>
                    <p className="text-foreground-950 font-bold text-lg">{item.valor}</p>
                    <p className="text-foreground-400 text-[9px]">{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Encuestas y concursos resumen */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5 space-y-4">
              <h4 className="text-foreground-950 font-bold text-sm flex items-center gap-2">
                <i className="ri-trophy-line text-accent-500"></i>
                Encuestas y Concursos
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-xs">Encuestas activas</span>
                  <span className="text-foreground-950 font-bold text-sm">{encuestas.filter((e) => e.activa).length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-xs">Concursos activos</span>
                  <span className="text-foreground-950 font-bold text-sm">{concursos.filter((c) => c.estado === 'activo').length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500 text-xs">Total participantes</span>
                  <span className="text-foreground-950 font-bold text-sm">{concursos.reduce((sum, c) => sum + c.participantes, 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'acciones' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {accionesConfig.map((acc) => (
                <div key={acc.tipo} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${acc.gradiente} flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${acc.icono} text-background-50 text-lg`}></i>
                  </div>
                  <p className="text-foreground-950 font-bold text-xs">{acc.label}</p>
                  <p className="text-foreground-400 text-[10px] mt-1">{acc.descripcion}</p>
                  <span className="inline-block mt-2 px-2 py-0.5 rounded-md bg-background-100 text-foreground-400 text-[10px] font-mono">
                    Tecla {acc.shortcut}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reglas' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reglasCentro.map((regla) => (
              <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                    <i className={`${regla.icono} text-background-50 text-sm`}></i>
                  </div>
                  <div>
                    <h4 className="text-foreground-950 font-bold text-sm mb-1">{regla.titulo}</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/centro-transmision"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-violet-500 text-background-50 font-bold text-sm cursor-pointer hover:from-rose-600 hover:to-violet-600 transition-all whitespace-nowrap"
          >
            <i className="ri-broadcast-line"></i>
            Abrir Centro de Transmisión
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-foreground-400 text-xs italic">
            "La Cabina debe ser el centro operativo principal. Todo lo importante, en una sola pantalla."
          </p>
        </div>
      </div>
    </section>
  );
}